import React from 'react';
import { Filter, FilterX } from 'lucide-react';

const FiltersSection = ({ 
  filters, 
  setFilters, 
  groupBy,
  setGroupBy,
  clients, 
  pages, 
  employees, 
  filteredRowsCount, 
  totalRowsCount,
  onClearFilters 
}) => {
  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <section className="max-w-full mx-auto px-8 py-6" aria-label="Data filters">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-800" aria-hidden="true" />
            <h2 className="text-base font-semibold text-gray-900">Filters</h2>
            {hasActiveFilters && (
              <span 
                className="px-2 py-1 bg-blue-600 text-white rounded-full text-[10px] font-medium"
                aria-live="polite"
                aria-label={`Showing ${filteredRowsCount} of ${totalRowsCount} records after filtering`}
              >
                {filteredRowsCount} of {totalRowsCount} records
              </span>
            )}
          </div>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-red-800 hover:text-red-900 hover:bg-red-50 focus:bg-red-50 focus:text-red-900 focus:outline-none focus:ring-4 focus:ring-red-200 rounded-lg transition-all duration-200"
              aria-label="Clear all active filters"
            >
              <FilterX className="w-4 h-4 mr-1" aria-hidden="true" />
              Clear Filters
            </button>
          )}
        </div>
        
  <div className="grid grid-cols-7 gap-4" role="group" aria-labelledby="filters-heading">
          <div className="sr-only" id="filters-heading">Data filtering options</div>

          {/* Code Filter (text, partial match) */}
          <div>
            <label htmlFor="code-filter" className="block text-xs font-medium text-gray-800 mb-2">Code</label>
            <input
              id="code-filter"
              type="text"
              value={filters.code}
              onChange={(e) => setFilters(prev => ({ ...prev, code: e.target.value }))}
              placeholder="e.g. ABC123"
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white placeholder-gray-400 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="code-filter-desc"
            />
            <div id="code-filter-desc" className="sr-only">Filter records whose code contains this text</div>
          </div>
          
          {/* Client Filter */}
          <div>
            <label htmlFor="client-filter" className="block text-xs font-medium text-gray-800 mb-2">Client</label>
            <select
              id="client-filter"
              value={filters.client}
              onChange={(e) => setFilters(prev => ({ ...prev, client: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="client-filter-desc"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
            <div id="client-filter-desc" className="sr-only">Filter records by client name</div>
          </div>

          {/* Page Filter */}
          <div>
            <label htmlFor="page-filter" className="block text-xs font-medium text-gray-800 mb-2">Page/Menu</label>
            <select
              id="page-filter"
              value={filters.page}
              onChange={(e) => setFilters(prev => ({ ...prev, page: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="page-filter-desc"
            >
              <option value="">All Pages</option>
              {pages.map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
            <div id="page-filter-desc" className="sr-only">Filter records by page or menu</div>
          </div>

          {/* Employee Filter */}
          <div>
            <label htmlFor="employee-filter" className="block text-xs font-medium text-gray-800 mb-2">Employee</label>
            <select
              id="employee-filter"
              value={filters.employee}
              onChange={(e) => setFilters(prev => ({ ...prev, employee: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="employee-filter-desc"
            >
              <option value="">All Employees</option>
              {employees.map(employee => (
                <option key={employee} value={employee}>{employee}</option>
              ))}
            </select>
            <div id="employee-filter-desc" className="sr-only">Filter records by employee name</div>
          </div>

          {/* Created From Date */}
          <div>
            <label htmlFor="created-from-filter" className="block text-xs font-medium text-gray-800 mb-2">Created From</label>
            <input
              id="created-from-filter"
              type="date"
              value={filters.createdFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, createdFrom: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="created-from-filter-desc"
            />
            <div id="created-from-filter-desc" className="sr-only">Filter records created from this date onwards</div>
          </div>

          {/* Created To Date */}
          <div>
            <label htmlFor="created-to-filter" className="block text-xs font-medium text-gray-800 mb-2">Created To</label>
            <input
              id="created-to-filter"
              type="date"
              value={filters.createdTo}
              onChange={(e) => setFilters(prev => ({ ...prev, createdTo: e.target.value }))}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="created-to-filter-desc"
            />
            <div id="created-to-filter-desc" className="sr-only">Filter records created up to this date</div>
          </div>

          {/* Grouping Selector */}
          <div>
            <label htmlFor="group-by" className="block text-xs font-medium text-gray-800 mb-2">Group By</label>
            <select
              id="group-by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-xs font-medium bg-white hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-200"
              aria-describedby="group-by-desc"
            >
              <option value="none">No Grouping</option>
              <option value="client">Client</option>
              <option value="page">Page/Menu</option>
            </select>
            <div id="group-by-desc" className="sr-only">Change table grouping mode</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FiltersSection;